import { inject, injectable } from "inversify";
import type { UrlService, UserTierService } from ".";
import type { IpfModule } from "..";
import { TYPES } from "../ioc/types";
import { SelectedTier } from "../models/app";
import { ApiAction, ApiUrl, HttpMethods } from "../models/http";
import { ServiceBase } from "./ServiceBase";

/**
 * URL service, used to generate and manipulate URLs.
 */
@injectable()
export class IpfUrlService extends ServiceBase implements UrlService {

	private static tierParamPlaceholders = ["{GroupId}", "{BranchId}", "{BrokerId}"];

	/**
	 * Initialises a new instance of the IpfUrlService class.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param userTierService User tier service, used to apply tier params to URLs.
	 */
	public constructor(
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.UserTierService) private userTierService: UserTierService
	) {
		super();
	}

	/**
	 * @inheritdoc
	 */
    public url(path: string, queryData?: object): string {

		if (path.startsWith("~/")) {

			path = path.replace("~/", "/");
		}
		else if (!path.startsWith("/")) {

			path = "/" + path;
		}

		let url = this.getPathBaseEnsuredUrl(path);

        if (queryData) {

            url += this.getQueryString(queryData);
        }

        return url;
    }

	/**
	 * @inheritdoc
	 */
    public apiUrl(apiUrl: ApiUrl): string {

        let path = apiUrl.path;

        if (path.startsWith("~/")) {

            path = path.replace("~/", "");
        }

        else if (path.startsWith("/")) {

            path = path.replace("/", "");
        }

		let url = this.getPathBaseEnsuredUrl([apiUrl.baseUrl, path].join("/"));

		if (!apiUrl.method) {

			apiUrl.method = HttpMethods.Get;
		}

		if (apiUrl.data) {

            url += this.getQueryString(apiUrl.data);
        }

        return url;
    }

	/**
	 * @inheritdoc
	 */
    public apiAction(apiAction: ApiAction): string {

		const urlFragments = [apiAction.baseUrl, apiAction.controller];

        if (apiAction.action) {

            urlFragments.push(apiAction.action);
        }

		let url = this.getPathBaseEnsuredUrl(urlFragments.join("/"));

		if (!apiAction.method) {

			apiAction.method = HttpMethods.Get;
		}

		if (apiAction.data && apiAction.method === HttpMethods.Get) {

            url += this.getQueryString(apiAction.data);
        }

        return url;
	}

	/**
	 * @inheritdoc
	 */
	public addQueryParam(queryString: string, paramName: string, paramValue: string) {

		if (queryString) {

			const urlParams = new URLSearchParams(queryString);
			urlParams.append(paramName, paramValue);
			return "?" + urlParams.toString();
		}
		else {

			return `?${paramName}=${paramValue}`;
		}
	}

	/**
	 * @inheritdoc
	 */
	public getQueryParams() {

		const query = window.location.search;
		let params: URLSearchParams;

		if (!query) {

			params = new URLSearchParams("?");
		}
		else {

			params = new URLSearchParams(query.slice(1));
		}

		return params;
	}

	/**
	 * @inheritdoc
	 */
	public getPageTierUrl(url?: string) {

		const query = this.getQueryString({
			GroupId: IpfUrlService.tierParamPlaceholders[0],
			BranchId: IpfUrlService.tierParamPlaceholders[1],
			BrokerId: IpfUrlService.tierParamPlaceholders[2],
		}, url?.indexOf("?") !== -1);

		return url ? url + query : query;
	}

	private getQueryString(data: any, append?: boolean): string {

		let queryParams = [];
		let pageTier = this.userTierService.getPageTier();

        for (const propertyName in data) {

			if (data.hasOwnProperty(propertyName)) {

				let paramValue = pageTier ? IpfUrlService.replaceTierParamValue(data[propertyName], pageTier) : data[propertyName];

				if (paramValue)
					queryParams.push(`${propertyName}=${paramValue}`);
            }
        }

		return queryParams.length > 0 ? (append === true ? "&" : "?") + queryParams.join("&") : "";
	}

	private getPathBaseEnsuredUrl(url: string) {

		let pathBase = this.getPathBase();

		if (pathBase && !url.startsWith(pathBase)) {

			if (!url.startsWith("/")) {

				pathBase += "/";
			}

			url = `${pathBase}${url}`;
		}

		return url;
	}

	private getPathBase() {

		return this.ipfModule.context.pathBase;
	}

	private static replaceTierParamValue(paramValue: string, tier?: SelectedTier) {

		if (IpfUrlService.tierParamPlaceholders.includes(paramValue)) {
			switch (paramValue) {
				case "{GroupId}":
					return tier?.group?.id ?? "";
				case "{BranchId}":
					return tier?.branch?.id ?? "";
				case "{BrokerId}":
					return tier?.broker?.id ?? "";
			}
		}

		return paramValue;
	}
}